import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import * as activityApi from '../endpoints/activity';
import type { FeedItem } from '@/types/feed';
import type { Paginated } from '@/types/pagination';

export const FEED_QUERY_KEYS = {
  all: ['feed'] as const,
  feed: () => [...FEED_QUERY_KEYS.all, 'activity'] as const,
  user: (userId: string) => [...FEED_QUERY_KEYS.all, 'user', userId] as const,
  menuItem: (menuItemId: string) => [...FEED_QUERY_KEYS.all, 'menu-item', menuItemId] as const,
  checkIn: (checkInId: string) => [...FEED_QUERY_KEYS.all, 'check-in', checkInId] as const,
  nearby: (lat: number, lon: number) => [...FEED_QUERY_KEYS.all, 'nearby', lat, lon] as const,
  business: (businessId: string, filters: Record<string, string | string[] | undefined>) =>
    [...FEED_QUERY_KEYS.all, 'business', businessId, filters] as const,
} as const;

/**
 * Domain-oriented query options for activity feed
 */

export const activityFeedQueryOptions = () =>
  infiniteQueryOptions({
    queryKey: FEED_QUERY_KEYS.feed(),
    queryFn: async ({ pageParam }): Promise<Paginated<FeedItem>> => {
      return activityApi.getFeed({ page: pageParam });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 10,
    refetchInterval: 60_000,
    meta: { persist: true },
  });

export const nearbyFeedQueryOptions = (latitude: number, longitude: number) =>
  infiniteQueryOptions({
    queryKey: FEED_QUERY_KEYS.nearby(latitude, longitude),
    queryFn: async ({ pageParam }): Promise<Paginated<FeedItem>> => {
      return activityApi.getNearbyFeed({ page: pageParam, latitude, longitude, radius_km: 25 });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 10,
    refetchInterval: 60_000,
    meta: { persist: true },
  });

export const userFeedQueryOptions = (userId: string) =>
  infiniteQueryOptions({
    queryKey: FEED_QUERY_KEYS.user(userId),
    queryFn: async ({ pageParam }): Promise<Paginated<FeedItem>> => {
      return activityApi.getUserFeed(userId, { page: pageParam });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 10,
  });

export const menuItemFeedQueryOptions = (menuItemId: string) =>
  infiniteQueryOptions({
    queryKey: FEED_QUERY_KEYS.menuItem(menuItemId),
    queryFn: async ({ pageParam }): Promise<Paginated<FeedItem>> => {
      return activityApi.getMenuItemFeed(menuItemId, { page: pageParam });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 10,
  });

export const businessFeedInfiniteQueryOptions = (
  businessId: string,
  filters: { rating_key?: string; menu_item_ids?: string[]; search?: string } = {}
) =>
  infiniteQueryOptions({
    queryKey: FEED_QUERY_KEYS.business(businessId, filters),
    queryFn: async ({ pageParam }): Promise<Paginated<FeedItem>> => {
      return activityApi.getBusinessFeed(businessId, { page: pageParam, ...filters });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    enabled: !!businessId,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 10,
  });

export const checkInDetailQueryOptions = (checkInId: string) =>
  queryOptions({
    queryKey: FEED_QUERY_KEYS.checkIn(checkInId),
    queryFn: () => activityApi.getCheckInFeedItem(checkInId),
    enabled: !!checkInId,
    staleTime: 1000 * 60 * 2,
  });
